import { useState, type FormEvent, type ReactElement } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { KeyRound, Pencil } from 'lucide-react'

import { LegacyModalSurface } from '~/components/overlays/ModalSurface'
import type { AppError } from '~/lib/result'
import {
  PROVIDER_CREDENTIAL_FIELDS,
  PROVIDER_LABELS,
  bankProviderCredentialsListQuery,
  useUpsertBankProviderCredential,
  type BankProviderCredential,
  type BankProviderName,
} from '~/features/bank-provider-credentials/queries'

/**
 * `/admin/finance/bank-provider-credentials` — Tink/Plaid/Lean API
 * credentials, managed here instead of Cloudflare Worker `wrangler secret
 * put` so ops can rotate them without CLI access. Read at request time by
 * the bank-feeds Worker.
 */
export const Route = createFileRoute('/admin/finance/bank-provider-credentials/')({
  component: BankProviderCredentialsPage,
})

function BankProviderCredentialsPage(): ReactElement {
  const listQuery = useQuery(bankProviderCredentialsListQuery())
  const [editing, setEditing] = useState<BankProviderCredential | null>(null)

  const providers = listQuery.data?.providers ?? []

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <h1 className="ka-page-header-title">Bank Feed Providers</h1>
          <p className="ka-page-header-subtitle">
            API credentials for Tink, Plaid, and Lean — read directly by the bank-feeds Worker.
          </p>
        </div>
      </header>

      {listQuery.error ? (
        <div className="ka-banner is-error" role="alert" aria-live="assertive" style={{ marginBottom: 12 }}>
          {errorToMessage(listQuery.error)}
        </div>
      ) : null}

      <table className="ka-table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Environment</th>
            <th>Status</th>
            <th>Credentials</th>
            <th>Last updated</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {listQuery.isPending ? (
            <tr>
              <td colSpan={6}>Loading…</td>
            </tr>
          ) : (
            providers.map((row) => (
              <tr key={row.provider}>
                <td>{PROVIDER_LABELS[row.provider]}</td>
                <td>{row.environment}</td>
                <td>
                  <span className={`ka-chip${row.configured && row.is_active ? ' ka-chip--success' : ''}`}>
                    {row.configured ? (row.is_active ? 'Configured' : 'Disabled') : 'Not configured'}
                  </span>
                </td>
                <td>
                  {Object.entries(row.credentials).map(([key, value]) => (
                    <div key={key} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {key}: {value}
                    </div>
                  ))}
                  {row.configured ? null : <span style={{ color: 'var(--ka-fg-muted, #888)' }}>—</span>}
                </td>
                <td>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</td>
                <td>
                  <button
                    type="button"
                    className="ka-btn ka-btn--secondary"
                    onClick={() => setEditing(row)}
                  >
                    <Pencil size={14} aria-hidden="true" /> Edit
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {editing ? (
        <EditCredentialDialog credential={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  )
}

function EditCredentialDialog({
  credential,
  onClose,
}: {
  readonly credential: BankProviderCredential
  readonly onClose: () => void
}): ReactElement {
  const provider: BankProviderName = credential.provider
  const fields = PROVIDER_CREDENTIAL_FIELDS[provider]
  const upsert = useUpsertBankProviderCredential()

  const [environment, setEnvironment] = useState(credential.environment)
  // First-time entry defaults to active — there's no reason to save working
  // credentials disabled. Editing an already-configured row keeps its state.
  const [isActive, setIsActive] = useState(credential.configured ? credential.is_active : true)
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const field of fields) {
      // Don't prefill sensitive values with the masked display string.
      initial[field.key] = field.sensitive ? '' : (credential.credentials[field.key] ?? '')
    }
    return initial
  })

  const canSubmit = fields.every((field) => values[field.key]?.trim())

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    if (!canSubmit) return
    upsert.mutate(
      { provider, environment, credentials: values, is_active: isActive },
      { onSuccess: onClose },
    )
  }

  return (
    <LegacyModalSurface onClose={onClose} title={`${PROVIDER_LABELS[provider]} credentials`} size="sm">
      <form className="ka-modal is-sm" onSubmit={handleSubmit}>
        <header className="ka-modal-head">
          <h2 className="ka-modal-head-title">
            <KeyRound size={16} aria-hidden="true" /> {PROVIDER_LABELS[provider]} credentials
          </h2>
        </header>
        <div className="ka-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="ka-field">
            <span className="ka-field-label">Environment</span>
            <div className="ka-field-input-wrap">
              <select
                className="ka-field-input"
                value={environment}
                onChange={(event) => setEnvironment(event.target.value as 'sandbox' | 'production')}
              >
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
          </label>

          {fields.map((field) => (
            <div className="ka-field" key={field.key}>
              <label className="ka-field-label" htmlFor={`cred-field-${field.key}`}>
                {field.label}
              </label>
              <div className="ka-field-input-wrap">
                <input
                  id={`cred-field-${field.key}`}
                  className="ka-field-input"
                  type={field.sensitive ? 'password' : 'text'}
                  value={values[field.key] ?? ''}
                  placeholder={
                    field.sensitive && credential.configured
                      ? `Currently ${credential.credentials[field.key] ?? 'unset'} — enter a new value to rotate`
                      : undefined
                  }
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, [field.key]: event.target.value }))
                  }
                />
              </div>
            </div>
          ))}

          <label className="ka-field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
            />
            <span className="ka-field-label">Active</span>
          </label>

          {upsert.error ? (
            <div className="ka-banner is-error" role="alert" aria-live="assertive">
              {errorToMessage(upsert.error)}
            </div>
          ) : null}
        </div>
        <footer className="ka-modal-foot">
          <button type="button" className="ka-btn ka-btn--ghost" onClick={onClose} disabled={upsert.isPending}>
            Cancel
          </button>
          <button type="submit" className="ka-btn ka-btn--primary" disabled={!canSubmit || upsert.isPending}>
            {upsert.isPending ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </form>
    </LegacyModalSurface>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
